import { Component, inject, signal, computed } from '@angular/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import {
  PeriformInputsReportOptions,
  PeriformInputsReportPresets,
  TrajectoryPlotSelection,
  validateReportOptions
} from '../../models/perifor-inputs-report-options.model';

interface PresetOption {
  label: string;
  value: string;
  options: PeriformInputsReportOptions;
}

@Component({
  selector: 'app-perifor-inputs-report-options-dialog',
  template: `
  <div class="flex flex-col">
    <p-scrollPanel [style]="{ width: '100%', height: '100%' }">
      <div class="flex flex-col space-y-4 max-h-[70vh]">
        <div class="mb-2">
          <label class="block">Quick Presets</label>
          <p-select
            class="w-full"
            [options]="presets"
            [(ngModel)]="selectedPreset"
            optionLabel="label"
            placeholder="Choose a preset"
            (onChange)="applyPreset($event.value)"
          ></p-select>
        </div>

        <p-divider></p-divider>

        <div class="space-y-4">
          <div>
            <h3 class="underline pb-3">Configuration</h3>
            <div class="space-y-2">
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includeWellConfigSummary" (ngModelChange)="onOptionChange('includeWellConfigSummary', $event)" [binary]="true" inputId="wellConfig" />
                <label for="wellConfig" class="ml-2">Well Configuration Summary</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includeStringSectionDetails" (ngModelChange)="onOptionChange('includeStringSectionDetails', $event)" [binary]="true" inputId="stringSections" />
                <label for="stringSections" class="ml-2">String Section Details</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includeAnnulusContents" (ngModelChange)="onOptionChange('includeAnnulusContents', $event)" [binary]="true" inputId="annulusContents" />
                <label for="annulusContents" class="ml-2">Annulus Contents</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includeCementingLanding" (ngModelChange)="onOptionChange('includeCementingLanding', $event)" [binary]="true" inputId="cementingLanding" />
                <label for="cementingLanding" class="ml-2">Cementing Landing (Pickup & Slack-Off Forces)</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includePackers" (ngModelChange)="onOptionChange('includePackers', $event)" [binary]="true" inputId="packers" />
                <label for="packers" class="ml-2">Packers</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includeExpansionJoints" (ngModelChange)="onOptionChange('includeExpansionJoints', $event)" [binary]="true" inputId="expansionJoints" />
                <label for="expansionJoints" class="ml-2">Expansion Joints</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includeFluidsSummaryTable" (ngModelChange)="onOptionChange('includeFluidsSummaryTable', $event)" [binary]="true" inputId="fluidsSummaryTable" />
                <label for="fluidsSummaryTable" class="ml-2">Fluids Summary Data</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includeDrillstringsTable" (ngModelChange)="onOptionChange('includeDrillstringsTable', $event)" [binary]="true" inputId="drillstringsTable" />
                <label for="drillstringsTable" class="ml-2">Drillstrings Summary Data</label>
              </div>
            </div>
          </div>

          <p-divider></p-divider>

          <div>
            <h3 class="underline pb-3">Plots & Diagrams</h3>
            <div class="space-y-2">
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includeTrajectory" (ngModelChange)="onOptionChange('includeTrajectory', $event)" [binary]="true" inputId="trajectory" />
                <label for="trajectory" class="ml-2 font-medium">Trajectory</label>
              </div>
              @if (options().includeTrajectory) {
                <div class="ml-8 space-y-2 border-l-2 border-gray-300 pl-4">
                  <div class="flex items-center">
                    <p-checkbox [ngModel]="options().trajectoryPlots.include3D" (ngModelChange)="onTrajectoryPlotChange('include3D', $event)" [binary]="true" inputId="traj3D" />
                    <label for="traj3D" class="ml-2">3D Isometric Plot</label>
                  </div>
                  <div class="flex items-center">
                    <p-checkbox [ngModel]="options().trajectoryPlots.includeNorthing" (ngModelChange)="onTrajectoryPlotChange('includeNorthing', $event)" [binary]="true" inputId="trajNorthing" />
                    <label for="trajNorthing" class="ml-2">Northing vs Measured Depth</label>
                  </div>
                  <div class="flex items-center">
                    <p-checkbox [ngModel]="options().trajectoryPlots.includeEasting" (ngModelChange)="onTrajectoryPlotChange('includeEasting', $event)" [binary]="true" inputId="trajEasting" />
                    <label for="trajEasting" class="ml-2">Easting vs Measured Depth</label>
                  </div>
                  <div class="flex items-center">
                    <p-checkbox [ngModel]="options().trajectoryPlots.includePlanView" (ngModelChange)="onTrajectoryPlotChange('includePlanView', $event)" [binary]="true" inputId="trajPlanView" />
                    <label for="trajPlanView" class="ml-2">Plan View (Northing vs Easting)</label>
                  </div>
                  <div class="flex items-center">
                    <p-checkbox [ngModel]="options().trajectoryPlots.includeDoglegSeverity" (ngModelChange)="onTrajectoryPlotChange('includeDoglegSeverity', $event)" [binary]="true" inputId="trajDogleg" />
                    <label for="trajDogleg" class="ml-2">Dogleg Severity vs Measured Depth</label>
                  </div>
                </div>
              }
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includeTrajectoryTable" (ngModelChange)="onOptionChange('includeTrajectoryTable', $event)" [binary]="true" inputId="trajectoryTable" />
                <label for="trajectoryTable" class="ml-2">Trajectory Data (Table)</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includeUdt" (ngModelChange)="onOptionChange('includeUdt', $event)" [binary]="true" inputId="udt" />
                <label for="udt" class="ml-2">Undisturbed Temperature (UDT) Plot</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includeUdtTable" (ngModelChange)="onOptionChange('includeUdtTable', $event)" [binary]="true" inputId="udtTable" />
                <label for="udtTable" class="ml-2">Undisturbed Temperature Data (Table)</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includeFormationTops" (ngModelChange)="onOptionChange('includeFormationTops', $event)" [binary]="true" inputId="formationTops" />
                <label for="formationTops" class="ml-2">Formation Tops Plots</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includeFormationTopsTable" (ngModelChange)="onOptionChange('includeFormationTopsTable', $event)" [binary]="true" inputId="formationTopsTable" />
                <label for="formationTopsTable" class="ml-2">Formation Tops Data (Table)</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includeWellSchematic" (ngModelChange)="onOptionChange('includeWellSchematic', $event)" [binary]="true" inputId="wellSchematic" />
                <label for="wellSchematic" class="ml-2">Well Schematic Diagram</label>
              </div>
            </div>
          </div>
        </div>

        @if (validationErrors().length > 0) {
          <div class="p-3 bg-red-50 border border-red-300 rounded">
            <h4 class="font-semibold text-red-800 mb-2">Validation Errors:</h4>
            <ul class="list-disc list-inside text-sm text-red-700">
              @for (error of validationErrors(); track error) {
                <li>{{ error }}</li>
              }
            </ul>
          </div>
        }

      </div>
    </p-scrollPanel>
    
    <div class="flex justify-end gap-2 pt-4">
      <p-button label="Cancel" outlined="true" class="outlined-button" size="small" severity="danger" (onClick)="close()"></p-button>
      <p-button
        outlined="true"
        label="Save Setup"
        class="outlined-button"
        size="small"
        severity="success"
        icon="pi pi-save" 
        (onClick)="generate()"
        [disabled]="validationErrors().length > 0"
      ></p-button>
    </div>
  </div>
  `,
  standalone: false
})
export class PeriformInputsReportOptionsDialogComponent {
  private readonly _dialogRef = inject(DynamicDialogRef);
  private readonly _dialogConfig = inject(DynamicDialogConfig);

  readonly options = signal<PeriformInputsReportOptions>(PeriformInputsReportPresets.Default);
  selectedPreset: PresetOption | null = null;

  readonly presets: PresetOption[] = [
    { label: 'None (No Sections Selected)', value: 'none', options: PeriformInputsReportPresets.None },
    { label: 'All Sections', value: 'all', options: PeriformInputsReportPresets.All },
    {
      label: 'Minimal',
      value: 'minimal',
      options: {
        ...PeriformInputsReportPresets.Default,
        includeWellConfigSummary: true,
        includeWellSchematic: true
      }
    },
    {
      label: 'Configuration Only',
      value: 'configOnly',
      options: {
        ...PeriformInputsReportPresets.Default,
        includeWellConfigSummary: true,
        includeStringSectionDetails: true,
        includeAnnulusContents: true,
        includeCementingLanding: true,
        includePackers: true
      }
    }
  ];

  // Validation errors
  readonly validationErrors = computed(() => {
    return validateReportOptions(this.options());
  });

  constructor() {
    const initialOptions = this._dialogConfig.data?.initialOptions as PeriformInputsReportOptions | null | undefined;

    if (initialOptions) {
      this.options.set(this.cloneOptions(initialOptions));
      this.selectedPreset = null;
      return;
    }

    const initialPreset = this._dialogConfig.data?.preset || 'none';
    this.selectedPreset = this.presets.find(p => p.value === initialPreset) || this.presets[0];
    this.options.set(this.cloneOptions(this.selectedPreset.options));
  }

  private cloneOptions(options: PeriformInputsReportOptions): PeriformInputsReportOptions {
    return JSON.parse(JSON.stringify(options));
  }

  applyPreset(preset: PresetOption): void {
    if (preset) {
      const clonedOptions = this.cloneOptions(preset.options);
      this.options.set(clonedOptions);
    }
  }

  onOptionChange(field: keyof PeriformInputsReportOptions, value: boolean): void {
    this.options.update(opts => ({
      ...opts,
      [field]: value
    }));
  }

  onTrajectoryPlotChange(field: keyof TrajectoryPlotSelection, value: boolean): void {
    this.options.update(opts => ({
      ...opts,
      trajectoryPlots: {
        ...opts.trajectoryPlots,
        [field]: value
      }
    }));
  }

  generate(): void {
    const finalOptions = this.options();
    this._dialogRef.close(finalOptions);
  }

  close(): void {
    this._dialogRef.close(null);
  }
}
